import React, { useEffect, useState } from "react";
import axios from "axios";
import { ArrowUpRight, ShoppingBag, RefreshCw, AlertCircle } from "lucide-react";
import { EBAY_URL } from "@/components/Header";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatPrice = (value, currency) => {
  if (!value) return null;
  const n = Number(value);
  if (Number.isNaN(n)) return `${value} ${currency || ""}`.trim();
  try {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: currency || "EUR",
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `${n.toFixed(2)} ${currency || "EUR"}`;
  }
};

const SkeletonCard = () => (
  <div className="border border-[var(--pz-border)] bg-white animate-pulse">
    <div className="aspect-square bg-[var(--pz-surface-alt)]" />
    <div className="p-4 space-y-2">
      <div className="h-3 bg-[var(--pz-surface-alt)] w-3/4" />
      <div className="h-3 bg-[var(--pz-surface-alt)] w-1/2" />
      <div className="h-5 bg-[var(--pz-surface-alt)] w-1/3 mt-3" />
    </div>
  </div>
);

const EbayProducts = () => {
  const [data, setData] = useState({ items: [], loading: true, error: null, configured: true });

  const load = async () => {
    setData((d) => ({ ...d, loading: true, error: null }));
    try {
      const res = await axios.get(`${API}/ebay/products?limit=8`);
      const payload = res.data || {};
      setData({
        items: payload.items || [],
        loading: false,
        error: payload.error || null,
        configured: payload.configured !== false,
        cached: payload.cached,
        stale: payload.stale,
      });
    } catch (err) {
      setData({
        items: [],
        loading: false,
        error: "network",
        configured: true,
      });
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Don't render the section at all if eBay isn't configured yet (admin-only state)
  if (!data.loading && !data.configured && data.items.length === 0) {
    return null;
  }

  return (
    <section
      id="produkte"
      data-testid="ebay-products-section"
      className="border-b border-[var(--pz-border)]"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <div className="pz-eyebrow-gold mb-4">Live aus dem Shop</div>
            <h2 className="font-display text-4xl sm:text-5xl font-black leading-[1.05] tracking-tight">
              Aktuelle{" "}
              <span className="pz-highlight">Angebote</span>
              <br />
              auf eBay.
            </h2>
          </div>
          <div className="flex gap-3">
            <button
              onClick={load}
              data-testid="ebay-refresh-button"
              className="pz-btn pz-btn-ghost text-sm"
              disabled={data.loading}
              aria-label="Angebote neu laden"
            >
              <RefreshCw size={16} className={data.loading ? "animate-spin" : ""} />
              Aktualisieren
            </button>
            <a
              href={EBAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="pz-btn pz-btn-primary"
            >
              Alle ansehen <ArrowUpRight size={18} />
            </a>
          </div>
        </div>

        {data.error && data.items.length === 0 && (
          <div className="border border-[var(--pz-border)] bg-[var(--pz-surface-alt)] p-8 text-center">
            <AlertCircle className="mx-auto mb-3 text-[var(--pz-accent)]" size={28} />
            <p className="font-semibold">Angebote können derzeit nicht geladen werden.</p>
            <p className="text-sm text-[var(--pz-text-muted)] mt-1">
              Schauen Sie direkt auf unserem eBay-Shop vorbei.
            </p>
            <a
              href={EBAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="pz-btn pz-btn-primary mt-5"
            >
              <ShoppingBag size={18} /> Zum eBay-Shop
            </a>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
          {data.loading &&
            Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)}

          {!data.loading &&
            data.items.map((p) => (
              <a
                key={p.id}
                href={p.item_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`ebay-product-${p.id}`}
                className="pz-card group border border-[var(--pz-border)] bg-white flex flex-col"
              >
                <div className="relative aspect-square overflow-hidden bg-[var(--pz-surface-alt)] pz-img-zoom">
                  {p.image_url ? (
                    <img
                      src={p.image_url}
                      alt={p.title || "eBay Angebot"}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-[var(--pz-text-muted)]">
                      <ShoppingBag size={32} />
                    </div>
                  )}
                  {p.condition && (
                    <span className="absolute top-2 left-2 text-[10px] uppercase tracking-[0.15em] font-bold bg-white/95 border border-[var(--pz-border)] px-2 py-1">
                      {p.condition}
                    </span>
                  )}
                </div>
                <div className="p-4 flex flex-col flex-1">
                  <h3 className="font-display font-bold text-sm leading-snug line-clamp-2 min-h-[2.5rem]">
                    {p.title}
                  </h3>
                  <div className="mt-3 flex items-end justify-between gap-3">
                    <div className="font-display font-black text-lg text-[var(--pz-text)]">
                      {formatPrice(p.price, p.currency)}
                    </div>
                    <ArrowUpRight
                      size={16}
                      className="text-[var(--pz-accent)] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    />
                  </div>
                </div>
              </a>
            ))}
        </div>

        {data.stale && (
          <p className="mt-6 text-xs text-[var(--pz-text-muted)] text-center">
            Aktuell zwischengespeicherte Angebote – eBay ist gerade nicht erreichbar.
          </p>
        )}
      </div>
    </section>
  );
};

export default EbayProducts;
